import { Api, ArrayUtil, UserSettings } from "@mcleod/core";
import {
    Button, Label, Layout, LayoutProps, PanelStaticError, SlideoutDecorator, Snackbar, Textbox
} from "@mcleod/components";
import { UserUtil } from "./UserUtil";

export class WebPasswordChange extends Layout {
    private buttonSave: Button;
    private endpointPath: string;
    private slideoutDecorator: SlideoutDecorator;
    private textboxConfirmPassword: Textbox;
    private textboxNewPassword: Textbox;
    private userId: string;
    private userName: string;

    constructor(endpointPath: string, userId: string, userName: string, props?: Partial<LayoutProps>) {
        super({
            ...props,
            id: "layoutWebPasswordChange",
            needsServerLayout: false,
            padding: 12
        });
        this.endpointPath = endpointPath;
        this.userId = userId;
        this.userName = userName;
        this.fill();
    }

    private fill() {
        // Only allow the password to be changed if either of the following are true:
        //  - The user is attempting to change their own password
        //  - The user is attempting to change another user's password, and the requesting user has permissions to the
        //    Users screen
        if (this.userId !== UserSettings.getUserId() && !UserUtil.userHasUserListAccess()) {
            const panelError = new PanelStaticError("Access denied due to permissions");
            this.add(panelError);
            return;
        }
        const labelInstructionsText = `To change the web password for user ${this.userName}, enter a new password in` +
            " the fields below, and click 'Save'.";
        const labelInstructions = new Label({
            id: "labelInstructions",
            marginBottom: 20,
            text: labelInstructionsText
        });
        this.textboxNewPassword = this.createPasswordTextbox("textboxNewPassword", "New Password");
        this.textboxConfirmPassword = this.createPasswordTextbox("textboxConfirmPassword", "Confirm New Password");
        this.add(labelInstructions, this.textboxNewPassword, this.textboxConfirmPassword);
    }

    private createPasswordTextbox(id: string, caption: string): Textbox {
        const textbox = new Textbox({
            caption,
            id,
            password: true,
            required: true
        });
        return textbox;
    }

    private createSaveButton(): Button {
        this.buttonSave = new Button({
            caption: "Save",
            default: true,
            id: "buttonSaveWebPasswordChange",
            onClick: () => this.save(),
            rowBreak: false
        });
        return this.buttonSave;
    }

    public show() {
        this.slideoutDecorator = new SlideoutDecorator({
            // Do not display Save button if the user doesn't have permissions to change user passwords.  In that case,
            // a static error will be displayed instead of the two password textboxes (which were not created).
            addlComponents: this.textboxNewPassword == null ? [] : [ this.createSaveButton() ],
            fillVerticalSpace: true,
            layout: this,
            title: "Set User Web Password",
            width: 400
        });
    }

    private async save() {
        if (this.validateSimple()) {
            try {
                this.buttonSave.busy = true;
                const response = await Api.post(this.endpointPath, {
                    confirm_password: this.textboxConfirmPassword.text,
                    new_password: this.textboxNewPassword.text,
                    user_id: this.userId
                });
                const responseRow = ArrayUtil.getFirstElement(response?.data);
                if (responseRow.success === true) {
                    this.slideoutDecorator.slideOut();
                } else {
                    Snackbar.showWarningSnackbar(responseRow.message);
                }
            } finally {
                this.buttonSave.busy = false;
            }
        }
    }
}
